import { useState } from "react";

import { m } from "@recobra/i18n/messages";
import {
  type ChecklistGroup,
  resolutionChecklist,
  type Task,
  type UpdateTaskFieldsInput,
  type SelectableLiabilityDeclineReason,
  requiresLiabilityDeclineReason,
  unsatisfiedRequirementKeys,
} from "@recobra/schemas/task";
import { nextTaskOptions, type Resolution } from "@recobra/schemas/task-catalog";

import { sortByLabel } from "../../lib";
import type { TaskRequirementContext } from "../task-details";
import { useTaskNotes } from "./useTaskNotes";

export type TaskCompletionInput = UpdateTaskFieldsInput & { status: "done"; solution: string };

export type TaskExecutionPolicy = { mode: "choose" } | { mode: "locked"; solution: string };

export const useTaskExecutionDraft = ({
  task,
  policy,
  context,
}: {
  task: Task;
  policy: TaskExecutionPolicy;
  context: TaskRequirementContext;
}) => {
  const notesDraft = useTaskNotes(task);
  const { notes } = notesDraft;
  const [declineReason, setDeclineReason] = useState<SelectableLiabilityDeclineReason | "">("");
  const resolutions = context.taskType?.resolutions ?? [];
  const options = selectable(resolutions, task.solution ?? undefined);
  const [draftSelection, setDraftSelection] = useState<string | null>(null);
  const selectableChoice = options.find(({ id }) => id === draftSelection)?.id;
  const selectedValue =
    policy.mode === "locked"
      ? policy.solution
      : (selectableChoice ?? initialTaskSolution(options, policy));
  const selectedResolution = resolutions.find(({ id }) => id === selectedValue);
  const next = useNextTaskChoice(task, context, selectedResolution);
  const pendingKeys = pendingRequirementKeys(task, context, selectedResolution);
  const needsNotes = Boolean(selectedResolution?.requiresNotes && !notes.trim());
  const needsDeclineReason = requiresLiabilityDeclineReason(task.system, selectedValue);
  const blockedReason = completionBlocker({
    selectedValue: selectedResolution ? selectedValue : "",
    pendingKeys,
    pendingGroup: context.taskType?.groups.find(({ id }) => pendingKeys.includes(id)),
    needsNotes,
    needsDeclineReason,
    declineReason,
  });

  return {
    selectedValue,
    selectSolution: setDraftSelection,
    options,
    selectedResolution,
    nextTasks: next.options,
    nextTaskType: next.nextTaskType,
    selectNextTask: next.select,
    ...notesDraft,
    declineReason,
    setDeclineReason,
    needsDeclineReason,
    canSubmit: !context.isLoading && !context.error && blockedReason === undefined,
    blockedReason,
    completionInput: {
      status: "done" as const,
      solution: selectedValue,
      ...(context.taskType?.collectsCompletionNotes !== false ? { notes } : {}),
      ...(needsDeclineReason && declineReason ? { declineReason } : {}),
      ...(next.options.length ? { nextTaskType: next.nextTaskType } : {}),
    },
  };
};

/** The resolution's next-task choices and the one picked, which falls back to the default when the resolution or the flow's options change. */
const useNextTaskChoice = (
  task: Task,
  context: TaskRequirementContext,
  resolution: Resolution | undefined,
) => {
  const [choice, setChoice] = useState<NextTaskChoice | null>(null);
  const candidates =
    resolution && context.taskType && task.contextType === "case"
      ? nextTaskOptions(context.taskType, resolution)
      : [];
  const options = candidates.some((taskType) => taskType !== null) ? candidates : [];
  const keepsChoice =
    choice !== null &&
    choice.resolutionId === resolution?.id &&
    options.includes(choice.nextTaskType);
  const nextTaskType = keepsChoice ? choice.nextTaskType : (resolution?.defaultNextTask ?? null);
  return {
    options,
    nextTaskType,
    select: (nextTask: string | null) =>
      resolution && setChoice({ resolutionId: resolution.id, nextTaskType: nextTask }),
  };
};

const pendingRequirementKeys = (
  task: Task,
  context: TaskRequirementContext,
  resolution: Resolution | undefined,
): string[] =>
  resolution
    ? unsatisfiedRequirementKeys({
        checklist: resolutionChecklist(context.taskType?.checklist ?? [], resolution.id),
        groups: context.taskType?.groups,
        requestedDocuments: task.requestedDocuments,
        progress: task.requirementProgress,
      })
    : [];

type NextTaskChoice = { resolutionId: string; nextTaskType: string | null };

const completionBlocker = ({
  selectedValue,
  pendingKeys,
  pendingGroup,
  needsNotes,
  needsDeclineReason,
  declineReason,
}: {
  selectedValue: string;
  pendingKeys: string[];
  pendingGroup: ChecklistGroup | undefined;
  needsNotes: boolean;
  needsDeclineReason: boolean;
  declineReason: SelectableLiabilityDeclineReason | "";
}): string | undefined => {
  if (!selectedValue) return m.task_complete_select_solution();
  if (pendingGroup)
    return m.task_complete_requirement_group_required({
      group: pendingGroup.label,
      minimum: String(pendingGroup.minimumSatisfied),
    });
  if (pendingKeys.length > 0) return m.task_complete_blocked_pending();
  if (needsNotes) return m.task_complete_note_required({ solution: selectedValue });
  if (needsDeclineReason && !declineReason) return m.task_decline_reason_required();
  return undefined;
};

/** Active resolutions, plus the one already recorded, by label. */
export const selectable = (
  resolutions: readonly Resolution[],
  alreadySelected: string | undefined,
): Resolution[] =>
  sortByLabel(
    resolutions.filter(({ active, id }) => active || id === alreadySelected),
    ({ label }) => label,
  );

export const initialTaskSolution = (
  resolutions: readonly Resolution[],
  policy: TaskExecutionPolicy,
): string => {
  if (policy.mode === "locked") return policy.solution;
  return resolutions.length === 1 ? resolutions[0]!.id : "";
};
